import React, { useState } from "react";
import { submitLeadQuietly } from "../lib/crm";
import leLogo from "../assets/le_logo.webp";
import ceLogo from "../assets/ce_logo.webp";

// ------------------------------------------------------------------
// UniThink × SAS — Clinical Programming Program Highlights Page
// React (plain JS) + TailwindCSS. Mobile-first & dependency-free.
// Mirrors the structure of the Data & AI page you approved.
// ------------------------------------------------------------------

// Identifies this form to the CRM: becomes the campaign name and the key its
// Lead Routing rule matches on.
const CRM_FORM_ID = "sas-clinical-programming";

export default function ClinicalProgram() {
  const ENQUIRY_ANCHOR = "#enquiry";

  // enquiry form state (same as the Data & AI page)
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    phone: "",
    city: "",
    interest: "SAS®️ Certified Clinical Trials Programmer",
    message: "",
    consent: false,
  });
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [gotcha, setGotcha] = useState(""); // bot trap; kept out of the Sheet payload

  const handleChange = (e) => {
  const { name, value, type, checked } = e.target;
  setForm({
    ...form,
    [name]: type === "checkbox" ? checked : value,
  });
};

// handleSubmit (fixing setSubmitted + reset)
const handleSubmit = async (e) => {
  e.preventDefault();
  const errs = validate();
  setErrors(errs);
  if (Object.keys(errs).length > 0) return;

  try {
    // Double-writing while the CRM proves itself: the Sheet is still the
    // source of truth, so only its outcome decides what the visitor sees.
    const [sheetResult] = await Promise.allSettled([
      fetch("https://script.google.com/macros/s/AKfycbzTqu_FRYCNYuzgI3XDy41HpOCt_XgMsW2hUY9qphXnGsgmZrhLPrihqdrDkTZZMpn4/exec", {
        method: "POST",
        mode: "no-cors",
        body: JSON.stringify(form),
        headers: { "Content-Type": "application/json" },
      }),
      submitLeadQuietly({ formId: CRM_FORM_ID, fields: { ...form, _gotcha: gotcha } }),
    ]);
    if (sheetResult.status === "rejected") throw sheetResult.reason;
    setSubmitted(true);
    setForm({
      fullName: "",
      email: "",
      phone: "",
      city: "",
      interest: "SAS Certified Clinical Trials Programmer",
      message: "",
      consent: false,
    });
  } catch (err) {
    alert("Error submitting form");
  }
};


  const validate = () => {
    const err = {};
    if (!form.fullName.trim()) err.fullName = "Please add your full name";
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/i.test(form.email)) err.email = "Enter a valid email";
    if (!/^[0-9+\-()\s]{8,}$/i.test(form.phone)) err.phone = "Enter a valid phone";
    if (!form.city.trim()) err.city = "Your city helps us plan batches";
    if (!form.consent) err.consent = "Please allow us to contact you";
    return err;
  };

  

  return (
    <div className="min-h-screen bg-white text-slate-900">
      {/* Top Bar */}
      <header className="sticky top-0 z-40 backdrop-blur bg-white/70 border-b border-slate-200">
  <div className="max-w-6xl mx-auto px-4 py-3 flex items-center justify-between">
    
    {/* Logo (left) */}
    <img
      src="/UniThink x SAS logo.png"
      alt="SAS Logo"
      className="h-30 w-36"
    />
    {/* Enquire Button (right) */}
    <a
      href="#enquiry"
      className="hidden sm:inline-flex px-4 py-2 rounded-2xl shadow-sm bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium"
    >
      Enquire Now
    </a>
  </div>
</header>

      {/* Hero */}
      <section className="relative">
        <div className="max-w-6xl mx-auto px-4 pt-10 pb-12 grid lg:grid-cols-2 gap-8 items-center">
          <div>
            <h1 className="text-3xl md:text-4xl font-extrabold leading-tight">SAS Academy for Clinical Programming </h1>
            <p className="mt-3 text-slate-700 text-base">Master Clinical SAS & Accelerate Your Career in Clinical Research</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <a href="#about" className="px-5 py-3 rounded-2xl bg-slate-900 text-white hover:bg-slate-800 font-medium shadow">About the Program</a>
              <a href={ENQUIRY_ANCHOR} className="px-5 py-3 rounded-2xl bg-white ring-1 ring-slate-200 hover:ring-slate-300 font-medium shadow-sm">Enquire Now</a>
            </div>
          </div>
          <div>
            <div
  className="relative aspect-[4/3] rounded-3xl ring-1 ring-slate-200  flex items-center justify-center bg-cover bg-center"
  style={{ backgroundImage: "url('/sas_cp.jpeg')" }} // image from /public folder
>
  {/* optional overlay for readability */}
  <div className="absolute inset-0 bg-black/30 rounded-3xl"></div>

</div>
          </div>
        </div>
      </section>

      {/* About SAS + About Course */}
      <section id="about" className="max-w-6xl mx-auto px-4 pb-2">
        <div className="grid md:grid-cols-2 gap-10">
  <div>
    <h2 className="text-2xl font-bold">About SAS</h2>
    <ul className="mt-4 mr-4 space-y-3 list-none">
      <li className="flex items-start gap-3">
        <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-blue-500 flex-shrink-0"></span>
        <p className="text-slate-700">
          Leader in Analytics & AI – SAS is the pioneer of analytics, helping organizations turn data into intelligence for better decisions.
        </p>
      </li>

      <li className="flex items-start gap-3">
        <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-blue-500 flex-shrink-0"></span>
        <p className="text-slate-700">
          Trusted by Industry Giants – 90% of Fortune 100 companies rely on SAS for analytics, AI, and data management.
        </p>
      </li>

      <li className="flex items-start gap-3">
        <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-blue-500 flex-shrink-0"></span>
        <p className="text-slate-700">
          Proven Across Industries – From healthcare and banking to manufacturing, retail, and government, SAS solutions address real-world challenges.
        </p>
      </li>

      <li className="flex items-start gap-3">
        <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-blue-500 flex-shrink-0"></span>
        <p className="text-slate-700">
          Comprehensive Platform – SAS offers end-to-end capabilities: data management, visualization, machine learning, AI, and model deployment.
        </p>
      </li>

      <li className="flex items-start gap-3">
        <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-blue-500 flex-shrink-0"></span>
        <p className="text-slate-700">
          Cloud-Native Innovation – With SAS Viya®️, organizations gain speed, scalability, and trusted results in a secure environment.
        </p>
      </li>

      <li className="flex items-start gap-3">
        <span className="mt-1.5 h-2.5 w-2.5 rounded-full bg-blue-500 flex-shrink-0"></span>
        <p className="text-slate-700">
          Growing Career Demand – Job postings requiring SAS skills have grown by nearly 17% in recent years, driving strong opportunities for certified professionals.
        </p>
      </li>
    </ul>
  </div>


          <div>
            <h2 className="text-2xl font-bold">About the Course</h2>
            <p className="mt-4 text-slate-700 leading-relaxed">
              This is the official SAS Clinical Trials Programming course in India. Tailored for individuals aspiring to build careers in pharmaceutical and
              healthcare analytics, the program provides official certification, industry‑recognized tools, and the credibility top employers value.
            </p>
            <p className="mt-3 text-slate-700 leading-relaxed">
              It is a hands‑on clinical course using real‑world datasets and lab‑based exercises. You will gain experience in Advanced SAS Programming,
              SDTM, ADaM, TLFs, and regulatory compliance — all aligned with global job roles.
            </p>
            <p className="mt-3 text-slate-700 leading-relaxed">
              The training offers a structured pathway into SAS Clinical Trials Programming, equipping learners with the applied skills and regulatory
              knowledge required for high‑demand roles in global clinical research.
            </p>
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="border-y border-slate-200 bg-slate-50/40">
        <div className="max-w-6xl mx-auto px-4 py-12">
          <h2 className="text-2xl md:text-3xl font-bold">Why Choose This Program?</h2>
          <ul className="mt-6 space-y-3 text-slate-700 max-w-4xl">
            <Bullet>Authorized Clinical SAS training in India — learn directly from SAS.</Bullet>
            <Bullet>Best‑in‑class coverage: Base SAS, Advanced SAS, SAS Macros, and SQL for clinical data processing & statistical programming.</Bullet>
            <Bullet>Hands‑on practice with real clinical data: CDISC, SDTM, ADaM standards, define.xml, and clinical trial reporting.</Bullet>
            <Bullet>Regulatory compliance & best practices: FDA, EMA, and global submission guidelines.</Bullet>
            <Bullet>Industry‑ready & job‑oriented: Prepare for roles like Clinical SAS Programmer, Clinical Data Analyst, and Biostatistician in leading pharma & CROs.</Bullet>
          </ul>

          <div className="mt-10 rounded-3xl ring-1 ring-slate-200 bg-white p-6 md:p-8 shadow-sm">
            <h3 className="text-xl font-semibold">How this program compares with unauthorized training providers</h3>
            <div className="mt-4 space-y-3 text-slate-700 leading-relaxed">
              <p>
                The SAS Academy for Clinical Programming is the only program authorized by SAS Institute. It offers globally recognized certification and
                a verifiable digital badge that employers actively value. Unauthorized providers are not recognized by SAS and do not offer official
                credentials — which can reduce credibility in the job market.
              </p>
              <p>
                Our training is delivered by SAS‑certified faculty and industry experts with direct experience in pharmaceutical analytics. We use licensed
                SAS software in a secure environment; many informal centers rely on unlicensed or no access, compromising quality and ethics.
              </p>
              <p>
                The curriculum maps directly to CRO and pharma job roles: practical training on SDTM/ADaM, define.xml creation, and TLF generation through
                real‑world tasks. These essentials are often missing or only theoretical in unrecognized programs.
              </p>
              <p>
                Most importantly, SAS credentials are trusted by top employers. Choosing the official SAS Academy gives you a credible, job‑aligned, and
                globally respected pathway into clinical research and statistical programming.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Program Tracks */}
      <section id="tracks" className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-2xl md:text-3xl font-bold text-center">Program Tracks</h2>
        <p className="mt-2 text-center text-slate-600 max-w-3xl mx-auto">
          Build both foundational and advanced clinical programming skills. Train with SDTM & ADaM using real clinical trial data, create Define.xml, and
          generate TLFs for regulatory submissions. Flexible weekend batches with hybrid support.
        </p>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5">
          <TrackCard
            title="SAS®️ Clinical Programming for Clinical Research"
            bullets={[
              "Learn Base SAS programming, data manipulation, and automation.",
              "Develop expertise in SAS Macros and SQL for clinical data processing.",
              "Prepare for the SAS Base Programming Certification exam."
            ]}
          />
          <TrackCard
            title="SAS®️ Clinical Data Analysis & Reporting"
            bullets={[
              "Gain insights into clinical research methodologies & statistical programming.",
              "Master CDISC standards (SDTM, ADaM) and regulatory submissions.",
              "Work on real‑world projects including TLFs and Define.xml generation."
            ]}
          />
          <TrackCard
            title="SAS®️ Certified Clinical Trials Programmer"
            bullets={[
              "Full access to the academy program & study resources.",
              "Guided preparation for certification exams.",
              "Career support for roles across pharma & CROs."
            ]}
          />
        </div>
      </section>

      {/* Who Should Enroll & Career Roles */}
      <section className="border-y border-slate-200 bg-slate-50/40">
        <div className="max-w-6xl mx-auto px-4 py-12 grid md:grid-cols-2 gap-10">
          <div>
            <h2 className="text-2xl font-bold">Who Should Enroll?</h2>
            <ul className="mt-4 space-y-2 text-slate-700">
              <Bullet>Freshers seeking clinical training to build careers in clinical research and data analysis.</Bullet>
              <Bullet>Life sciences and pharmacy graduates looking to enter clinical research.</Bullet>
              <Bullet>Statisticians, data analysts, and programmers interested in healthcare analytics.</Bullet>
              <Bullet>Pharma professionals seeking SAS certification and career advancement.</Bullet>
            </ul>
          </div>
          <div>
            <h2 className="text-2xl ml-10 font-bold">Career Pathways & Job Roles</h2>
            <ul className="mt-4 space-y-2 ml-12 text-slate-700">
              <Bullet>Clinical SAS Programmer</Bullet>
              <Bullet>Clinical Data Manager</Bullet>
              <Bullet>Biostatistician</Bullet>
              <Bullet>Pharmacovigilance Analyst</Bullet>
              <Bullet>Statistical Programmer</Bullet>
            </ul>
          </div>
        </div>
      </section>

      {/* Certification & Badges */}
      <section id="badges" className="max-w-6xl mx-auto px-4 py-12">
        <h2 className="text-2xl md:text-3xl font-bold">SAS Certification & Digital Badges</h2>
        <p className="mt-3 text-slate-700 max-w-3xl">
          Complete the program to earn globally recognized SAS credentials aligned with industry hiring standards. Showcase your achievement with
          verifiable digital badges on LinkedIn, resumes, and job applications.
        </p>
        <ul className="mt-4 space-y-2 text-slate-700">
          <Bullet>Earn a globally recognized SAS®️ Certification — stand out in the job market.</Bullet>
          <Bullet>Get a Digital Badge — share your skills across professional networks.</Bullet>
        </ul>


       <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
  <div className="flex items-start gap-4 p-5 rounded-2xl border shadow">
    <img
      src={leLogo}
      alt="Learn"
      className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 object-contain rounded-xl"
    />
    <div>
      <h3 className="text-lg font-semibold">LEARN</h3>
      <p className="text-slate-600 text-sm mt-1">
        Awarded to those who complete and pass official SAS training, across formats.
      </p>
    </div>
  </div>

  <div className="flex items-start gap-4 p-5 rounded-2xl border shadow">
    <img
      src={ceLogo}
      alt="Certified"
      className="h-12 w-12 sm:h-14 sm:w-14 md:h-16 md:w-16 object-contain rounded-xl"
    />
    <div>
      <h3 className="text-lg font-semibold">CERTIFIED</h3>
      <p className="text-slate-600 text-sm mt-1">
        Awarded to those who show proficiency by passing one or more SAS certification exams.
      </p>
    </div>
  </div>
</div>


      </section>

      {/* Enquiry Form */}
      <section id="enquiry" className="max-w-3xl mx-auto px-4 py-12">
        <div className="rounded-3xl ring-1 ring-slate-200 bg-white p-6 md:p-8 shadow-xl">
          <h2 className="text-2xl font-bold">Enquire Now</h2>
          <p className="mt-2 text-slate-600 text-sm">Share your details and we’ll send curriculum, schedules, and fees for the Clinical Programming track.</p>

          {submitted ? (
            <div className="mt-6 rounded-2xl bg-green-50 ring-1 ring-green-200 p-4">
              <p className="font-medium text-green-800">Thank you! Your enquiry has been received.</p>
              <p className="text-sm text-green-800/80">We’ll contact you shortly via phone/email.</p>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="mt-6 grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Bot trap: hidden from people, so anything typed here is spam. */}
              <input
                type="text"
                name="_gotcha"
                value={gotcha}
                onChange={(e) => setGotcha(e.target.value)}
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                style={{ position: "absolute", left: "-9999px", width: 1, height: 1, opacity: 0 }}
              />
              <div>
                <label className="text-sm font-medium">Full Name *</label>
                <input name="fullName" value={form.fullName} onChange={handleChange} className={`mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 ${errors.fullName ? "border-red-300 ring-red-100" : "border-slate-300 ring-blue-100"}`} placeholder="Your full name" />
                {errors.fullName ? <p className="text-xs text-red-600 mt-1">{errors.fullName}</p> : null}
              </div>
              <div>
                <label className="text-sm font-medium">Email *</label>
                <input name="email" value={form.email} onChange={handleChange} className={`mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 ${errors.email ? "border-red-300 ring-red-100" : "border-slate-300 ring-blue-100"}`} placeholder="you@example.com" />
                {errors.email ? <p className="text-xs text-red-600 mt-1">{errors.email}</p> : null}
              </div>
              <div>
                <label className="text-sm font-medium">Phone / WhatsApp *</label>
                <input name="phone" value={form.phone} onChange={handleChange} className={`mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 ${errors.phone ? "border-red-300 ring-red-100" : "border-slate-300 ring-blue-100"}`} placeholder="+91 …" />
                {errors.phone ? <p className="text-xs text-red-600 mt-1">{errors.phone}</p> : null}
              </div>
              <div>
                <label className="text-sm font-medium">City *</label>
                <input name="city" value={form.city} onChange={handleChange} className={`mt-1 w-full rounded-xl border px-3 py-2 focus:outline-none focus:ring-2 ${errors.city ? "border-red-300 ring-red-100" : "border-slate-300 ring-blue-100"}`} placeholder="Your city" />
                {errors.city ? <p className="text-xs text-red-600 mt-1">{errors.city}</p> : null}
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium">Interested Program</label>
                <input name="interest" value={form.interest} onChange={handleChange} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100" />
              </div>
              <div className="md:col-span-2">
                <label className="text-sm font-medium">Questions / Notes</label>
                <textarea name="message" value={form.message} onChange={handleChange} rows={4} className="mt-1 w-full rounded-xl border border-slate-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-100" placeholder="Any specific questions (fees, schedule, certification, etc.)" />
              </div>
              <div className="md:col-span-2 flex items-start gap-3">
                <input id="consent" type="checkbox" name="consent" checked={form.consent} onChange={handleChange} className="mt-1 h-4 w-4 rounded border-slate-300" />
                <label htmlFor="consent" className="text-sm text-slate-700">I agree to be contacted by UniThink about SAS programs via phone/WhatsApp/email.</label>
              </div>
              {errors.consent ? <p className="md:col-span-2 text-xs text-red-600 -mt-2">{errors.consent}</p> : null}

              <div className="md:col-span-2 flex gap-3">
                <button type="submit" className="px-5 py-3 rounded-2xl bg-blue-600 hover:bg-blue-700 text-white font-medium shadow">Submit Enquiry</button>
                <a href="#tracks" className="px-5 py-3 rounded-2xl bg-white ring-1 ring-slate-200 hover:ring-slate-300 font-medium shadow-sm">View Tracks</a>
              </div>
            </form>
          )}
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-slate-200">
        <div className="max-w-6xl mx-auto px-4 py-8 text-sm text-slate-600 grid md:grid-cols-2 gap-4">
          <p>©️ {new Date().getFullYear()} UniThink Education International Pvt. Ltd. All rights reserved.</p>
          <p className="md:text-right">UniThink — Your Trusted SAS®️ Education Partner.</p>
        </div>
      </footer>
    </div>
  );
}

// -------------
// Re-usable bits
// -------------

function Bullet({ children }) {
  return (
    <li className="flex gap-3"><span className="mt-2 h-2.5 w-2.5 rounded-full bg-blue-600"></span><span className="text-slate-700">{children}</span></li>
  );
}

function TrackCard({ title, bullets }) {
  return (
    <div className="rounded-3xl ring-1 ring-slate-200 bg-white p-6 shadow-sm">
      <h3 className="font-semibold">{title}</h3>
      <ul className="mt-3 space-y-2 text-sm text-slate-700">
        {bullets.map((b, i) => (
          <li key={i} className="flex gap-2"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-slate-400"></span><span>{b}</span></li>
        ))}
      </ul>
    </div>
  );
}

function BadgeCard({ badgeTitle, desc }) {
  return (
    <div className="rounded-3xl ring-1 ring-slate-200 bg-white p-6 text-center shadow-sm">
      <div className="mx-auto h-14 w-14 rounded-2xl bg-slate-900 text-white grid place-content-center text-sm font-bold">{badgeTitle.slice(0,2)}</div>
      <h3 className="mt-3 font-semibold tracking-wide">{badgeTitle}</h3>
      <p className="mt-2 text-sm text-slate-700">{desc}</p>
    </div>
  );
}